<template>
  <div :class="b([type, { vertical }])">
    <div
      ref="wrap"
      :class="[
        b('wrap', [position, { scrollable }]),
        { 'om-hairline-top-bottom': type === 'line' && !vertical }
      ]"
    >
      <div :class="b('nav', [type, { vertical }])" ref="nav">
        <div v-if="type === 'line'" :class="b('nav-bar')" :style="navBarStyle" />
        <div
          v-for="(tab, index) in tabs"
          ref="tabs"
          class="om-tab-item"
          :class="{
            'om-tab-item-active': index === curActive,
            'om-tab-item-disabled': tab.disabled,
            'om-tab-item-vertical': vertical
          }"
          @click="onClick(index)">
          <om-node v-if="tab.$slots.title" :node="tab.$slots.title" />
          <div v-else>
            <div v-if="isShowInfo(tab.info)"" class="om-tab-item-info">{{ tab.info }}</div>
            <span class="om-ellipsis" >{{ tab.title }}</span>
          </div>
        </div>
      </div>
    </div>
    <div :class="b('content')" ref="content">
      <slot />
    </div>
    <div :class="b('more')"></div>
  </div>
</template>

<script>
import create from '../utils/create';
import { raf } from '../utils/raf';
import { on, off } from '../utils/event';
import OmNode from '../utils/node';
import scrollUtils from '../utils/scroll';
import Touch from '../mixins/touch';
import { isDef } from '../utils'

export default create({
  name: 'tab-group',

  mixins: [Touch],

  components: {
    OmNode
  },

  model: {
    prop: 'active'
  },

  props: {
    sticky: Boolean,
    active: {
      type: [Number, String],
      default: 0
    },
    type: {
      type: String,
      default: 'line'
    },
    duration: {
      type: Number,
      default: 0.2
    },
    swipeThreshold: {
      type: Number,
      default: 4
    },
    swipeable: Boolean,
    vertical: Boolean,
  },

  data() {
    return {
      tabs: [],
      position: 'content-top',
      curActive: 0,
      navBarStyle: {}
    };
  },

  computed: {
    // whether the nav is scrollable
    scrollable() {
      return this.tabs.length > this.swipeThreshold;
    }
  },

  watch: {
    active(val) {
      if (val !== this.curActive) {
        this.correctActive(val);
      }
    },

    tabs(tabs) {
      this.correctActive(this.curActive || this.active);
      this.setNavBar()
    },

    curActive() {
      this.scrollIntoView();
      this.setNavBar();

      // scroll to correct position
      if (this.position === 'page-top' || this.position === 'content-bottom') {
        scrollUtils.setScrollTop(this.scrollEl, scrollUtils.getElementTop(this.$el));
      }
    },

    sticky(isSticky) {
      this.scrollHandler(isSticky);
    }
  },

  mounted() {
    this.correctActive(this.active);
    this.setNavBar();

    this.$nextTick(() => {
      if (this.sticky) {
        this.scrollHandler(true);
      }
      if (this.swipeable) {
        this.swipeableHandler(true);
      }
      this.scrollIntoView();
    });
  },

  beforeDestroy() {
    /* istanbul ignore next */
    if (this.sticky) {
      this.scrollHandler(false);
    }
    /* istanbul ignore next */
    if (this.swipeable) {
      this.swipeableHandler(false);
    }
  },

  methods: {
    isDef,
    // whether to bind sticky listener
    scrollHandler(init) {
      this.scrollEl = this.scrollEl || scrollUtils.getScrollEventTarget(this.$el);
      (init ? on : off)(this.scrollEl, 'scroll', this.onScroll, true);
      if (init) {
        this.onScroll();
      }
    },

    // whether to bind content swipe listener
    swipeableHandler(init) {
      const swipeableEl = this.$refs.content;

      (init ? on : off)(swipeableEl, 'touchstart', this.touchStart);
      (init ? on : off)(swipeableEl, 'touchmove', this.touchMove);
      (init ? on : off)(swipeableEl, 'touchend', this.onTouchEnd);
      (init ? on : off)(swipeableEl, 'touchcancel', this.onTouchEnd);
    },

    // watch swipe touch end
    onTouchEnd() {
      const { direction, deltaX, curActive } = this;
      const minSwipeDistance = 50;

      /* istanbul ignore else */
      if (direction === 'horizontal' && this.offsetX >= minSwipeDistance) {
        /* istanbul ignore else */
        if (deltaX > 0 && curActive !== 0) {
          this.setCurActive(curActive - 1);
        } else if (deltaX < 0 && curActive !== this.tabs.length - 1) {
          this.setCurActive(curActive + 1);
        }
      }
    },

    // adjust tab position
    onScroll() {
      const scrollTop = scrollUtils.getScrollTop(this.scrollEl);
      const elTopToPageTop = scrollUtils.getElementTop(this.$el);
      const elBottomToPageTop = elTopToPageTop + this.$el.offsetHeight - this.$refs.wrap.offsetHeight;
      if (scrollTop > elBottomToPageTop) {
        this.position = 'content-bottom';
      } else if (scrollTop > elTopToPageTop) {
        this.position = 'page-top';
      } else {
        this.position = 'content-top';
      }
    },

    // update nav bar style
    setNavBar() {
      this.$nextTick(() => {
        if (!this.$refs.tabs || this.$refs.tabs.length === 0) {
          return;
        }

        const tab = this.$refs.tabs[this.curActive];
        if (this.vertical) {
          let top = 0
          if (tab.offsetTop) {
            top = tab.offsetTop + (tab.offsetHeight - 20) / 2
          } else {
            top = (tab.offsetHeight - 20) / 2
          }
          this.navBarStyle = {
            height: '20px', //${tab.offsetWidth || 0}
            width: '4px',
            transform: `translate(0, ${top || 0}px)`,
            transitionDuration: `${this.duration}s`
          };
        } else {
          let left = 0
          if (tab.offsetLeft) {
            left = tab.offsetLeft + (tab.offsetWidth - 20) / 2
          } else if (tab.offsetWidth) {
            left = (tab.offsetWidth - 20) / 2
          }
          this.navBarStyle = {
            width: '20px', //${tab.offsetWidth || 0}
            height: '4px',
            transform: `translate(${left || 0}px, 0)`,
            transitionDuration: `${this.duration}s`
          };
        }
      });
    },

    // correct the value of active
    correctActive(active) {
      active = +active;
      const exist = this.tabs.some(tab => tab.index === active);
      const defaultActive = (this.tabs[0] || {}).index || 0;
      this.setCurActive(exist ? active : defaultActive);
    },

    setCurActive(active) {
      this.curActive = active;
      this.$emit('input', active);
    },

    // emit event when clicked
    onClick(index) {
      const { title, disabled } = this.tabs[index];
      if (disabled) {
        this.$emit('disabled', index, title);
      } else {
        this.$emit('click', index, title);
        this.setCurActive(index);
      }
    },

    // scroll active tab into view
    scrollIntoView() {
      if (!this.scrollable || !this.$refs.tabs) {
        return;
      }

      const tab = this.$refs.tabs[this.curActive];
      const { nav } = this.$refs;
      const { scrollLeft, offsetWidth: navWidth } = nav;
      const { offsetLeft, offsetWidth: tabWidth } = tab;

      this.scrollTo(nav, scrollLeft, offsetLeft - (navWidth - tabWidth) / 2);
    },

    // animate the scrollLeft of nav
    scrollTo(el, from, to) {
      let count = 0;
      const frames = Math.round(this.duration * 1000 / 16);
      const animate = () => {
        el.scrollLeft += (to - from) / frames;
        /* istanbul ignore next */
        if (++count < frames) {
          raf(animate);
        }
      };
      animate();
    },
    isShowInfo (info) {
      return isDef(info) && info > 0
    }
  }
});
</script>



// WEBPACK FOOTER //
// src/plugins/jscom/omui/components/tab-group/index.vue